import { useEffect } from "react";
import { FormEvent, useState } from "react";
import { ActionMeta, SingleValue } from "react-select";

import { UserData } from "../../../api/types/user.type";
import { updateUser } from "../../../api/users/updateUser";
import { translationsContent } from "../../../localization/translations";
import { useLanguage } from "../../../store/lang.context";
import { useUser } from "../../../store/user.context";
import { translate } from "../../../utils/translate";
import { updateAccountSchema } from "../../../utils/validations";
import DarkButton from "../../ui/button/DarkButton";
import Button from "../../ui/button/SimpleButton";
import SimpleDropdown, { OptionProps } from "../../ui/dropdown/SimpleDropdown";
import RadioButtonAlt from "../../ui/input/RadioButtonAlt";
import Input from "../../ui/input/SimpleInput";
import classes from "./personalInformation.module.scss";
import { SubmitPersonalInformationType } from "./types/submit.type";

const days: OptionProps[] = [];
for (let i = 1; i <= 31; i++) days[i] = { value: i, label: i };

const years: OptionProps[] = [];
for (let i = 0; i <= 102; i++) years[i] = { value: 2022 - i, label: 2022 - i };

const submitObject: SubmitPersonalInformationType = {
  first_name: { value: "", isValid: true },
  last_name: { value: "", isValid: true },
  email: { value: "", isValid: true },
  phone_number: { value: "", isValid: true },
  country: { value: "", isValid: true },
  country_order: { value: "", isValid: true },
  birthday: { value: "", isValid: true },
  birthday_day: { value: "", isValid: true },
  birthday_month: { value: "", isValid: true },
  birthday_year: { value: "", isValid: true },
  allow_newsletter: { value: false, isValid: true },
  allow_newsletter_sms: { value: false, isValid: true },
};

const PersonalInformation = () => {
  const [formData, setFormData] =
    useState<SubmitPersonalInformationType>(submitObject);
  const { user, setUser } = useUser();
  const [disabled, setDisabled] = useState(false);
  const [notValid, setNotValid] = useState(false);
  const [error, setError] = useState(false);
  const [errorLabel, setErrorLabel] = useState("");

  const { language } = useLanguage();

  const months: OptionProps[] = [
    {
      value: "1",
      label: translate(translationsContent, "month1", language).toString(),
    },
    {
      value: "2",
      label: translate(translationsContent, "month2", language).toString(),
    },
    {
      value: "3",
      label: translate(translationsContent, "month3", language).toString(),
    },
    {
      value: "4",
      label: translate(translationsContent, "month4", language).toString(),
    },
    {
      value: "5",
      label: translate(translationsContent, "month5", language).toString(),
    },
    {
      value: "6",
      label: translate(translationsContent, "month6", language).toString(),
    },
    {
      value: "7",
      label: translate(translationsContent, "month7", language).toString(),
    },
    {
      value: "8",
      label: translate(translationsContent, "month8", language).toString(),
    },
    {
      value: "9",
      label: translate(translationsContent, "month9", language).toString(),
    },
    {
      value: "10",
      label: translate(translationsContent, "month10", language).toString(),
    },
    {
      value: "11",
      label: translate(translationsContent, "month11", language).toString(),
    },
    {
      value: "12",
      label: translate(translationsContent, "month12", language).toString(),
    },
  ];

  const formatUser = (user: UserData) => {
    const birthday = user && user.birthday ? user.birthday.split("-") : [];
    return {
      first_name: { value: user.first_name, isValid: true },
      last_name: { value: user.last_name, isValid: true },
      email: { value: user.email, isValid: true },
      phone_number: { value: user.phone_number, isValid: true },
      country: { value: user.country.name, isValid: true },
      country_order: { value: user.country_order.name, isValid: true },
      birthday: { value: user.birthday, isValid: true },
      birthday_year: { value: parseInt(birthday[0]) + "", isValid: true },
      birthday_month: {
        value: parseInt(birthday[1]) + "",
        isValid: true,
      },
      birthday_day: { value: parseInt(birthday[2]) + "", isValid: true },
      allow_newsletter: { value: user.allow_newsletter, isValid: true },
      allow_newsletter_sms: { value: user.allow_newsletter_sms, isValid: true },
    };
  };

  const resetForm = () => {
    if (user) setFormData(formatUser(user));
  };

  useEffect(() => {
    if (user) setFormData(formatUser(user));
  }, [user]);

  useEffect(() => {
    if (user) {
      setDisabled(
        JSON.stringify(formatUser(user)) === JSON.stringify(formData)
      );
    }
    let ok = true;
    Object.keys(formData).forEach((key) => {
      if (
        formData[key as keyof SubmitPersonalInformationType].isValid === false
      ) {
        setNotValid(true);
        ok = false;
        return;
      }
    });
    if (ok) {
      setNotValid(false);
    }
  }, [formData, user]);

  const handleDropdownInputChange = (
    newValue: SingleValue<OptionProps>,
    actionMeta: ActionMeta<OptionProps>
  ) => {
    const value = newValue?.value + "";
    const identifier: keyof SubmitPersonalInformationType =
      actionMeta?.name as keyof SubmitPersonalInformationType;

    setFormData((prevState) => ({
      ...prevState,
      [identifier]: {
        value: value,
        isValid:
          identifier !== "country" && identifier !== "country_order"
            ? !updateAccountSchema.validate({ [identifier]: value }).error
            : true,
      },
    }));
  };

  const handleInputChange = (
    identifier: keyof SubmitPersonalInformationType,
    value: string | boolean
  ) => {
    if (
      identifier === "allow_newsletter" ||
      identifier === "allow_newsletter_sms"
    ) {
      setFormData((FormDataPreviousState) => ({
        ...FormDataPreviousState,
        [identifier]: {
          value: value === "YES" ? true : false,
          isValid: true,
        },
      }));
    } else {
      setFormData((FormDataPreviousState) => ({
        ...FormDataPreviousState,
        [identifier]: {
          value: value,
          isValid: !updateAccountSchema.validate({ [identifier]: value }).error,
        },
      }));
    }
  };

  const handleSubmitForm = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!user) return;
    const input = {
      first_name: formData.first_name.value,
      last_name: formData.last_name.value,
      email: formData.email.value,
      phone_number: formData.phone_number.value,
      country: formData.country.value,
      country_order: formData.country.value,
      birthday:
        formData.birthday_year.value +
        "-" +
        formData.birthday_month.value +
        "-" +
        formData.birthday_day.value,
      allow_newsletter: formData.allow_newsletter.value,
      allow_newsletter_sms: formData.allow_newsletter_sms.value,
    };

    const validate = updateAccountSchema.validate(input);

    if (validate.error) {
      setError(true);
      for (const key in formData) {
        const validationItem =
          formData[key as keyof SubmitPersonalInformationType];
        const tmpValidate = updateAccountSchema.validate({
          [key]: validationItem.value,
        });
        if (tmpValidate.error)
          setFormData((prevState) => ({
            ...prevState,
            [key]: {
              isValid: false,
            },
          }));
      }
      return;
    }
    setError(false);

    const { success, data, error } = await updateUser(input, user.id);
    if (success && data) {
      setUser(data);
    } else {
      setError(true);
      for (const key in error) {
        if (key == "allow_newsletter") continue;
        const item = formData[key as keyof SubmitPersonalInformationType];
        setFormData((prevState) => ({
          ...prevState,
          [key as keyof SubmitPersonalInformationType]: {
            value: item.value,
            isValid: false,
          },
        }));
        if (key === "email") setErrorLabel("Email déjà existant");
      }
    }
  };

  return (
    <div className={classes.container}>
      <h2 className={classes.formTitle}>
        {translate(translationsContent, "myProfileHeader", language)}
      </h2>
      <div className={classes.formContainer}>
        <form
          className={classes.form}
          autoComplete={"off"}
          onSubmit={(event) => {
            handleSubmitForm(event);
          }}
        >
          <div className={classes.inputs}>
            <div className={classes.name}>
              <Input<SubmitPersonalInformationType, "first_name">
                isValid={formData.first_name.isValid}
                error={error}
                type="text"
                value={formData.first_name.value}
                identifier="first_name"
                placeholder={`${translate(
                  translationsContent,
                  "firstNamePlaceholder",
                  language
                )}`}
                required
                label={`${translate(
                  translationsContent,
                  "firstNameLabel",
                  language
                )}`}
                onChange={handleInputChange}
              />
            </div>
            <div className={classes.nameDivider} />
            <div className={classes.name}>
              <Input<SubmitPersonalInformationType, "last_name">
                label={`${translate(
                  translationsContent,
                  "lastNameLabel",
                  language
                )}`}
                isValid={formData.last_name.isValid}
                error={error}
                type="text"
                value={formData.last_name.value}
                identifier="last_name"
                placeholder={`${translate(
                  translationsContent,
                  "lastNamePlaceholder",
                  language
                )}`}
                required
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className={classes.dropdown}>
            <p>{translate(translationsContent, "birthdateLabel", language)}</p>
            <div className={classes.birthdayDropdown}>
              <div className={classes.item}>
                <SimpleDropdown
                  isValid={formData.birthday_day.isValid}
                  error={error}
                  options={days}
                  defaultValue={
                    days.filter(
                      (d) => d.value == formData.birthday_day.value
                    )[0]
                  }
                  value={
                    days.filter(
                      (d) => d.value == formData.birthday_day.value
                    )[0]
                  }
                  name="birthday_day"
                  placeholder={`${translate(
                    translationsContent,
                    "birthdateDayPlaceholder",
                    language
                  )}`}
                  onChange={handleDropdownInputChange}
                />
              </div>
              <div className={classes.item}>
                <SimpleDropdown
                  isValid={formData.birthday_month.isValid}
                  error={error}
                  options={months}
                  defaultValue={
                    months.filter(
                      (m) => m.value == formData.birthday_month.value
                    )[0]
                  }
                  value={
                    months.filter(
                      (m) => m.value == formData.birthday_month.value
                    )[0]
                  }
                  name="birthday_month"
                  placeholder={`${translate(
                    translationsContent,
                    "birthdateMonthPlaceholder",
                    language
                  )}`}
                  onChange={handleDropdownInputChange}
                />
              </div>
              <div className={classes.item}>
                <SimpleDropdown
                  isValid={formData.birthday_year.isValid}
                  error={error}
                  options={years}
                  defaultValue={
                    years.filter(
                      (y) => y.value == formData.birthday_year.value
                    )[0]
                  }
                  value={
                    years.filter(
                      (y) => y.value == formData.birthday_year.value
                    )[0]
                  }
                  name="birthday_year"
                  placeholder={`${translate(
                    translationsContent,
                    "birthdateYearPlaceholder",
                    language
                  )}`}
                  onChange={handleDropdownInputChange}
                />
              </div>
            </div>
          </div>

          <Input<SubmitPersonalInformationType, "phone_number">
            label={`${translate(
              translationsContent,
              "telephoneLabel",
              language
            )}`}
            isValid={formData.phone_number.isValid}
            error={error}
            type="tel"
            value={formData.phone_number.value}
            identifier="phone_number"
            placeholder={`${translate(
              translationsContent,
              "telephonePlaceholder",
              language
            )}`}
            required
            onChange={handleInputChange}
          />

          <Input<SubmitPersonalInformationType, "email">
            label={`${translate(translationsContent, "email", language)}`}
            isValid={formData.email.isValid}
            error={error}
            type="email"
            identifier="email"
            placeholder={`${translate(
              translationsContent,
              "setEmail",
              language
            )}`}
            required
            value={formData.email.value}
            onChange={handleInputChange}
            readonly
          />

          <div className={classes.radioButtonsContainer}>
            <p>{translate(translationsContent, "newsletter", language)}</p>
            <div className={classes.radioButtons}>
              <p>{translate(translationsContent, "byEmail", language)}</p>
              <RadioButtonAlt<SubmitPersonalInformationType, "allow_newsletter">
                label={`${translate(
                  translationsContent,
                  "newsletterYes",
                  language
                )}`}
                value="YES"
                name="allow_newsletter"
                identifier="allow_newsletter"
                checked={formData.allow_newsletter.value}
                onChange={handleInputChange}
              />
              <RadioButtonAlt<SubmitPersonalInformationType, "allow_newsletter">
                label={`${translate(
                  translationsContent,
                  "newsletterNo",
                  language
                )}`}
                value="NO"
                name="allow_newsletter"
                identifier="allow_newsletter"
                checked={!formData.allow_newsletter.value}
                onChange={handleInputChange}
              />
            </div>
            <div className={classes.radioButtons}>
              <p>{translate(translationsContent, "bySMS", language)}</p>
              <RadioButtonAlt<
                SubmitPersonalInformationType,
                "allow_newsletter_sms"
              >
                label={`${translate(
                  translationsContent,
                  "newsletterYes",
                  language
                )}`}
                value="YES"
                name="allow_newsletter_sms"
                identifier="allow_newsletter_sms"
                checked={formData.allow_newsletter_sms.value}
                onChange={handleInputChange}
              />
              <RadioButtonAlt<
                SubmitPersonalInformationType,
                "allow_newsletter_sms"
              >
                label={`${translate(
                  translationsContent,
                  "newsletterNo",
                  language
                )}`}
                value="NO"
                name="allow_newsletter_sms"
                identifier="allow_newsletter_sms"
                checked={!formData.allow_newsletter_sms.value}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className={classes.divider}></div>

          {user && (
            <div className={classes.buttonGroup}>
              <div className={classes.cancelButtonContainer}>
                <DarkButton
                  disabled={disabled}
                  label={`${translate(
                    translationsContent,
                    "annulerButton",
                    language
                  )}`}
                  onClick={resetForm}
                />
              </div>
              <div className={classes.submitButtonContainer}>
                <Button
                  disabled={disabled || notValid}
                  label={`${translate(
                    translationsContent,
                    "validerButton",
                    language
                  )}`}
                  onClick={() => {}}
                />
              </div>
            </div>
          )}
        </form>
        {error && (
          <div className={classes.notValid}>
            {<p>{errorLabel}</p>}
            {!formData.first_name.isValid && (
              <p>
                {translate(
                  translationsContent,
                  "signupFirstNameError",
                  language
                )}
              </p>
            )}
            {!formData.last_name.isValid && (
              <p>
                {translate(
                  translationsContent,
                  "signupLastNameError",
                  language
                )}
              </p>
            )}
            {!formData.phone_number.isValid && (
              <p>
                {translate(
                  translationsContent,
                  "signupPhonenumberError",
                  language
                )}
              </p>
            )}
            {!formData.country.isValid && (
              <p>
                {translate(
                  translationsContent,
                  "signupOriginCountryError",
                  language
                )}
              </p>
            )}
            {!formData.country_order.isValid && (
              <p>
                {translate(
                  translationsContent,
                  "signupOrderCountryError",
                  language
                )}
              </p>
            )}
            {(!formData.birthday_day.isValid ||
              !formData.birthday_month.isValid ||
              !formData.birthday_year.isValid) && (
              <p>
                {translate(
                  translationsContent,
                  "signupBirthdateError",
                  language
                )}
              </p>
            )}
            {!formData.email.isValid && (
              <p>
                {translate(translationsContent, "signupEmailError", language)}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonalInformation;
